import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Bell, WifiOff, Download, Apple, Share, PlusSquare, CheckCircle2, Sparkles, KeyRound } from "lucide-react";
import { StoreBadges, useMobileApps } from "../../components/StoreBadges";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

function PhoneMock() {
  return (
    <div className="relative mx-auto w-[270px] h-[560px] rounded-[2.6rem] border-[6px] border-[#1c2b4a] bg-[#0b1a35] shadow-2xl shadow-black/50 overflow-hidden" data-testid="landing-phone-mock">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black/80" />
      <div className="pt-10 px-4 space-y-3 text-[11px]">
        <div className="flex items-center justify-between"><span className="text-slate-400">Aujourd'hui</span><span className="text-brass font-semibold">Casanéo</span></div>
        <div className="rounded-xl bg-white/[0.06] border border-white/10 p-3">
          <p className="text-slate-400 uppercase tracking-wider text-[9px]">Arrivées du jour</p>
          <p className="text-white font-semibold mt-1">3 arrivées · 2 départs</p>
          <div className="mt-2 space-y-1.5">
            {[["Villa Azur", "16 h · Clés boîte n° 4", "bg-emerald-400"], ["Riviera 22", "18 h · Solde réglé", "bg-sky-400"], ["Le Mandarin", "20 h · Caution en attente", "bg-amber-400"]].map(([n, d, c]) => (
              <div key={n} className="flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${c}`} /><span className="text-slate-200 flex-1 truncate">{n}</span><span className="text-slate-500">{d}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-xl bg-brass/15 border border-brass/30 p-3 flex items-start gap-2">
          <Bell className="h-3.5 w-3.5 text-brass mt-0.5" />
          <div><p className="text-white font-medium">Ménage terminé — Villa Azur</p><p className="text-slate-400">Photos jointes · 12:40</p></div>
        </div>
        <div className="rounded-xl bg-white/[0.06] border border-white/10 p-3 flex items-start gap-2">
          <KeyRound className="h-3.5 w-3.5 text-sky-300 mt-0.5" />
          <div><p className="text-white font-medium">Message J-1 envoyé</p><p className="text-slate-400">Marie D. · WhatsApp · heure d'arrivée + livret</p></div>
        </div>
        <div className="rounded-xl bg-white/[0.06] border border-white/10 p-3">
          <p className="text-slate-400 uppercase tracking-wider text-[9px]">Ce mois</p>
          <div className="flex items-end justify-between mt-1"><p className="text-white text-lg font-semibold">33 611 €</p><p className="text-emerald-400">+12 %</p></div>
          <div className="mt-2 h-8 flex items-end gap-1">{[3, 5, 4, 7, 6, 8, 9, 7, 10, 8, 11, 12].map((h, i) => <span key={i} className="flex-1 rounded-sm bg-brass/60" style={{ height: `${h * 2.4}px` }} />)}</div>
        </div>
      </div>
    </div>
  );
}

export function MobileApp() {
  const { data: apps } = useMobileApps();
  const [prompt, setPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);
  useEffect(() => {
    setIos(isIOS());
    setInstalled(window.matchMedia("(display-mode: standalone)").matches);
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  const install = async () => {
    if (prompt) { prompt.prompt(); await prompt.userChoice; setPrompt(null); return; }
    setShowIos(true);
  };
  return (
    <section id="application" className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28 grid gap-12 lg:grid-cols-[1.1fr_1fr] items-center" data-testid="landing-mobile">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-brass inline-flex items-center gap-2"><Smartphone className="h-4 w-4" /> Application mobile</p>
        <h2 className="display mt-4 text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">Votre conciergerie dans la poche, 100 % pensée pour le téléphone.</h2>
        <p className="mt-5 text-sm md:text-base text-slate-300 leading-relaxed">
          Arrivées du jour, ménages, cartes refusées, dégâts signalés : tout arrive sur votre écran, où que vous soyez. L'application s'installe en 10 secondes
          depuis votre navigateur — <span className="text-white">sans passer par un store</span> — se met à jour toute seule et garde votre planning accessible même sans réseau.
        </p>
        <ul className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          {[[Bell, "Notifications push", "Réservations, paiements, ménage"], [WifiOff, "Mode hors-ligne", "Planning et fiches consultables"], [Sparkles, "Toujours à jour", "Aucune mise à jour à installer"]].map(([Icon, t, d]) => (
            <li key={t} className="rounded-xl border border-white/10 bg-white/[0.03] p-3"><Icon className="h-4 w-4 text-brass" /><p className="text-white mt-2 font-medium">{t}</p><p className="text-xs text-slate-400">{d}</p></li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {installed ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-5 py-3 text-sm text-emerald-300" data-testid="landing-app-installed"><CheckCircle2 className="h-4 w-4" /> Application déjà installée</span>
          ) : (
            <button type="button" onClick={install} data-testid="landing-app-install" className="inline-flex items-center gap-2 rounded-full bg-brass hover:bg-brass/90 text-[#0b1a35] font-semibold px-6 py-3.5 text-sm transition-transform hover:scale-[1.02] shadow-xl shadow-brass/20">
              {ios ? <Apple className="h-4 w-4" /> : <Download className="h-4 w-4" />} {ios ? "Installer sur iPhone" : "Installer l'application"}
            </button>
          )}
          <StoreBadges apps={apps} testid="landing-store-badges" />
        </div>
        {showIos && !installed && (
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-slate-300" data-testid="landing-app-ios-steps">
            <p className="text-white font-medium mb-2">{ios ? "Sur iPhone / iPad (Safari)" : "Depuis votre téléphone"}</p>
            <ol className="space-y-1.5">
              <li className="flex items-center gap-2"><span className="text-brass font-mono text-xs">1</span> Ouvrez <span className="text-white">casaneo.pro</span> {ios ? "dans Safari" : "dans Chrome"}</li>
              <li className="flex items-center gap-2"><span className="text-brass font-mono text-xs">2</span> Touchez {ios ? <><Share className="h-3.5 w-3.5" /> Partager</> : "le menu ⋮"}</li>
              <li className="flex items-center gap-2"><span className="text-brass font-mono text-xs">3</span> <PlusSquare className="h-3.5 w-3.5" /> « Sur l'écran d'accueil » puis Ajouter</li>
            </ol>
          </div>
        )}
        <p className="mt-4 text-xs text-slate-500">Vos agents ménage ont leur propre accès, limité à leurs départs et signalements — sans jamais voir les montants.</p>
      </div>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
        <PhoneMock />
      </motion.div>
    </section>
  );
}
